/**
 * Faceted filtering behaviour.
 *
 * Progressive enhancement only. With JS disabled the form in facets.liquid is a
 * plain GET form with checkboxes and a submit button, and filtering works.
 * This file adds:
 *   - auto-submit on change, with the grid swapped in place instead of a reload
 *   - dual-range sliders for numeric facets (Flex, Height, Paddle, Price)
 *   - search-within-facet for long value lists such as Curve
 *
 * The sliders never submit their own values. They tick the underlying
 * checkboxes (or fill the price number inputs), so the submitted state is
 * always the same shape the no-JS path would produce.
 */
(() => {
  'use strict';

  const REGIONS = ['[data-facet-region="grid"]', '[data-facet-region="facets"]', '[data-facet-region="toolbar"]'];

  /* ------------------------------------------------------------- helpers --- */

  const num = (s) => parseFloat(String(s).replace(/[^0-9.\-]/g, ''));

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  /* -------------------------------------------------------- range slider --- */

  /**
   * Two overlapping <input type=range> elements behave as one dual-thumb
   * control. `values` is the ordered list of selectable stops, so the thumbs
   * always land on a value the catalogue actually has — no dead zones.
   */
  class DualRange {
    constructor(root) {
      this.root = root;
      this.lower = root.querySelector('[data-range-lower]');
      this.upper = root.querySelector('[data-range-upper]');
      this.fill = root.querySelector('[data-range-fill]');
      this.readout = root.querySelector('[data-range-readout]');
      this.mode = root.dataset.facetRange;
      if (!this.lower || !this.upper) return;

      this.mode === 'price' ? this.initPrice() : this.initList();

      [this.lower, this.upper].forEach((el) => {
        el.addEventListener('input', () => this.onInput());
        el.addEventListener('change', () => this.commit());
      });

      this.paint();
    }

    /* Price uses the platform's own min/max number inputs. */
    initPrice() {
      this.minInput = this.root.querySelector('[data-range-min-input]');
      this.maxInput = this.root.querySelector('[data-range-max-input]');
      this.min = num(this.root.dataset.min) || 0;
      this.max = num(this.root.dataset.max) || 0;
      this.values = null;

      // Keep the visible number fields authoritative if a shopper types.
      [this.minInput, this.maxInput].forEach((el) => {
        if (!el) return;
        el.addEventListener('change', () => {
          this.lower.value = num(this.minInput.value || this.min);
          this.upper.value = num(this.maxInput.value || this.max);
          this.normalise();
          this.paint();
        });
      });
    }

    /* Numeric list facets step through the real option values. */
    initList() {
      this.container = this.root.parentElement.querySelector('[data-facet-values]');
      this.checks = Array.from(this.container ? this.container.querySelectorAll('.facet__check') : []);
      // Height is stored inconsistently as `65` and `65.0`, and 18 values exist
      // in both forms. They are two filter values but one point on the scale,
      // so the stops are de-duplicated. commit() still ticks every checkbox
      // whose value falls in range, so both forms get submitted.
      this.values = [
        ...new Set(
          this.checks.map((el) => num(el.dataset.value)).filter((n) => !Number.isNaN(n))
        ),
      ].sort((a, b) => a - b);

      if (!this.values.length) return;

      this.lower.min = this.upper.min = 0;
      this.lower.max = this.upper.max = this.values.length - 1;
      this.lower.step = this.upper.step = 1;

      // Seed the thumbs from whatever is already checked.
      const checkedNums = this.checks
        .filter((el) => el.querySelector('input').checked)
        .map((el) => num(el.dataset.value))
        .sort((a, b) => a - b);

      const lo = checkedNums.length ? checkedNums[0] : this.values[0];
      const hi = checkedNums.length ? checkedNums[checkedNums.length - 1] : this.values[this.values.length - 1];
      this.lower.value = this.values.indexOf(lo);
      this.upper.value = this.values.indexOf(hi);
    }

    normalise() {
      let a = num(this.lower.value);
      let b = num(this.upper.value);
      if (a > b) {
        [a, b] = [b, a];
        this.lower.value = a;
        this.upper.value = b;
      }
    }

    bounds() {
      this.normalise();
      if (this.values) {
        return [this.values[num(this.lower.value)], this.values[num(this.upper.value)]];
      }
      return [num(this.lower.value), num(this.upper.value)];
    }

    onInput() {
      this.paint();
    }

    paint() {
      this.normalise();
      const lo = num(this.lower.value);
      const hi = num(this.upper.value);
      const min = num(this.lower.min);
      const max = num(this.lower.max) || 1;
      const span = max - min || 1;

      if (this.fill) {
        this.fill.style.insetInlineStart = `${((lo - min) / span) * 100}%`;
        this.fill.style.inlineSize = `${((hi - lo) / span) * 100}%`;
      }

      const [a, b] = this.bounds();
      if (this.readout) {
        this.readout.textContent = a === b ? `${a}` : `${a} – ${b}`;
      }
      if (this.mode === 'price') {
        if (this.minInput) this.minInput.value = a > this.min ? a : '';
        if (this.maxInput) this.maxInput.value = b < this.max ? b : '';
      }
    }

    /** Translate thumb positions into the checkbox state that gets submitted. */
    commit() {
      if (this.values) {
        const [a, b] = this.bounds();
        const isFullRange = a === this.values[0] && b === this.values[this.values.length - 1];
        this.checks.forEach((el) => {
          const input = el.querySelector('input');
          const v = num(el.dataset.value);
          // Non-numeric strays (N/A, ???) share the facet but not the scale.
          // Leave whatever the shopper chose for them alone.
          if (Number.isNaN(v)) return;
          // A full-width range is the same as no filter — don't send 26 params.
          input.checked = isFullRange ? false : v >= a && v <= b;
        });
      }
      this.root.dispatchEvent(new CustomEvent('facet:change', { bubbles: true }));
    }
  }

  /* -------------------------------------------------------- value search --- */

  function wireValueSearch(form) {
    form.querySelectorAll('[data-facet-search]').forEach((input) => {
      const group = input.closest('[data-facet-group]');
      const rows = Array.from(group.querySelectorAll('.facet__check'));
      input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase();
        let shown = 0;
        rows.forEach((row) => {
          const hit = !q || (row.dataset.label || '').includes(q);
          row.hidden = !hit;
          if (hit) shown += 1;
        });
        group.classList.toggle('facet--no-matches', shown === 0);
      });
    });
  }

  /* ------------------------------------------------------------- fetching --- */

  let inflight = null;

  async function applyFilters(form, pushState = true) {
    const data = new FormData(form);
    const params = new URLSearchParams();
    for (const [k, v] of data.entries()) {
      if (v !== '' && v != null) params.append(k, v);
    }
    const url = `${form.action.split('?')[0]}${params.toString() ? `?${params}` : ''}`;

    document.querySelectorAll('[data-facet-region]').forEach((el) => el.setAttribute('aria-busy', 'true'));

    if (inflight) inflight.abort();
    inflight = new AbortController();

    try {
      const res = await fetch(url, { signal: inflight.signal });
      if (!res.ok) throw new Error(`${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      REGIONS.forEach((sel) => {
        const next = doc.querySelector(sel);
        const cur = document.querySelector(sel);
        if (next && cur) cur.replaceWith(next);
      });

      if (pushState) history.pushState({ facets: true }, '', url);
      rewire();
      const grid = document.querySelector('[data-facet-region="grid"]');
      if (grid) grid.setAttribute('tabindex', '-1');
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Filtering must never dead-end. Fall back to a real navigation.
      window.location.assign(url);
    } finally {
      document.querySelectorAll('[data-facet-region]').forEach((el) => el.removeAttribute('aria-busy'));
    }
  }

  /* --------------------------------------------------------------- wiring --- */

  function rewire() {
    const form = document.querySelector('[data-facet-form]');
    if (!form) return;

    form.querySelectorAll('[data-facet-range]').forEach((el) => new DualRange(el));
    wireValueSearch(form);

    const submit = debounce(() => applyFilters(form), 250);

    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-range-lower], [data-range-upper], [data-facet-search]')) return;
      submit();
    });
    form.addEventListener('facet:change', submit);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      applyFilters(form);
    });

    const clear = form.querySelector('[data-facet-clear]');
    if (clear) {
      clear.addEventListener('click', (e) => {
        e.preventDefault();
        form.querySelectorAll('input[type="checkbox"]').forEach((c) => (c.checked = false));
        form.querySelectorAll('input[type="number"]').forEach((n) => (n.value = ''));
        applyFilters(form);
      });
    }

    // Sort control lives outside the facet form but shares its URL state.
    const sort = document.querySelector('[data-sort-by]');
    if (sort) {
      sort.addEventListener('change', () => {
        let hidden = form.querySelector('input[name="sort_by"]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'sort_by';
          form.appendChild(hidden);
        }
        hidden.value = sort.value;
        applyFilters(form);
      });
    }

    form.querySelectorAll('noscript').forEach((n) => n.remove());
  }

  window.addEventListener('popstate', () => window.location.reload());
  document.addEventListener('DOMContentLoaded', rewire);
  if (document.readyState !== 'loading') rewire();
})();

/**
 * The facet panel is a <details> so it works as a real drawer on small screens
 * with no JS. But a <details> without `open` has zero height regardless of what
 * CSS says about its children, which collapsed the desktop sidebar.
 *
 * So the markup ships `open` (correct for desktop, and a usable no-JS fallback
 * on mobile), and this collapses it on small screens where it should start as a
 * button instead.
 */
(() => {
  'use strict';
    // Must match the sidebar breakpoint in catalogue.css.
  const mq = window.matchMedia('(min-width: 68rem)');
  const sync = () => {
    document.querySelectorAll('.coll__drawer').forEach((d) => {
      d.open = mq.matches;
    });
  };
  mq.addEventListener('change', sync);
  document.addEventListener('DOMContentLoaded', sync);
  if (document.readyState !== 'loading') sync();
})();
